import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { categoriaProfissaoLabels, type CampoProfissao, type Profissao } from '@/lib/profissoes';
import DocumentosDoRegistro from '@/components/feature/DocumentosDoRegistro';
import AnexoDoItem from '@/components/feature/AnexoDoItem';
import { listarDocumentos, type Documento } from '@/lib/documentosRepo';
import { diasEmPeriodo, periodoTexto, type PeriodoParcial } from '@/lib/periodo';
import { useCatalogo, type TreinamentoCatalogo } from '@/lib/catalogo';
import { dataValida, MENSAGEM_DATA, noFuturo } from '@/lib/datas';
import {
  mascaraCpf,
  mascaraRg,
  mascaraTelefone,
  MENSAGEM,
  validarCpf,
  validarEmail,
  validarRg,
  validarTelefone,
} from '@/lib/mascaras';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { calcularDataVencimento, obterStatusTreinamento } from '@/pages/dashboard/shared-data';
import { useAuth } from '@/hooks/useAuth';

interface MergedCampo extends CampoProfissao {
  profissoes: { id: string; nome: string }[];
}

interface MergedDocumento {
  id: string;
  nome: string;
  obrigatorio: boolean;
  profissoes: { id: string; nome: string }[];
}

interface MergedTreinamento {
  id: string;
  nome: string;
  vigenciaDias: number;
  /** O Periodo como o catalogo o guarda, para a tela nao reconstrui-lo dos dias. */
  vigencia: PeriodoParcial;
  obrigatorio: boolean;
  descricao?: string;
  profissoes: { id: string; nome: string }[];
}

function mergeCampos(profissoes: Profissao[]): MergedCampo[] {
  const mapa = new Map<string, MergedCampo>();
  profissoes.forEach((prof) => {
    prof.campos.forEach((campo) => {
      if (mapa.has(campo.id)) {
        const existente = mapa.get(campo.id)!;
        existente.profissoes.push({ id: prof.id, nome: prof.nome });
        if (campo.obrigatorio) existente.obrigatorio = true;
      } else {
        mapa.set(campo.id, {
          ...campo,
          profissoes: [{ id: prof.id, nome: prof.nome }],
        });
      }
    });
  });
  return Array.from(mapa.values());
}

function mergeDocumentos(profissoes: Profissao[]): MergedDocumento[] {
  const mapa = new Map<string, MergedDocumento>();
  profissoes.forEach((prof) => {
    prof.documentos.forEach((doc) => {
      if (mapa.has(doc.id)) {
        const existente = mapa.get(doc.id)!;
        existente.profissoes.push({ id: prof.id, nome: prof.nome });
        if (doc.obrigatorio) existente.obrigatorio = true;
      } else {
        mapa.set(doc.id, {
          ...doc,
          profissoes: [{ id: prof.id, nome: prof.nome }],
        });
      }
    });
  });
  return Array.from(mapa.values());
}

/**
 * Treinamentos exigidos pelas profissões selecionadas.
 *
 * A exigência vem do catálogo `treinamentos` (tabela), e não de uma cópia dentro
 * da profissão: corrigir a vigência de uma norma passa a valer para o próximo
 * registro de todo mundo, sem deploy.
 */
function mergeTreinamentos(
  profissoes: Profissao[],
  catalogo: TreinamentoCatalogo[],
): MergedTreinamento[] {
  const mapa = new Map<string, MergedTreinamento>();

  profissoes.forEach((prof) => {
    catalogo
      // O vínculo virou N:N (migration 0047): a mesma norma pode valer para
      // várias profissões, e `aplica_a_qualquer_profissao` cobre a que vale
      // para todo funcionário — que antes não aparecia para ninguém.
      .filter((t) => t.aplica_a_qualquer_profissao || t.profissoes.includes(prof.id))
      .forEach((treino) => {
        const existente = mapa.get(treino.id);
        if (existente) {
          existente.profissoes.push({ id: prof.id, nome: prof.nome });
          if (treino.obrigatorio) existente.obrigatorio = true;
          return;
        }
        mapa.set(treino.id, {
          id: treino.id,
          nome: treino.nome,
          vigenciaDias: treino.vigencia_dias,
          vigencia: { valor: treino.vigencia_valor, unidade: treino.vigencia_unidade },
          obrigatorio: treino.obrigatorio,
          descricao: treino.descricao || undefined,
          profissoes: [{ id: prof.id, nome: prof.nome }],
        });
      });
  });

  return Array.from(mapa.values());
}

function renderCampoDinamico(
  campo: MergedCampo,
  valor: string,
  onChange: (id: string, value: string) => void,
) {
  const inputBase =
    'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

  switch (campo.tipo) {
    case 'date':
      return (
        <input
          type="date"
          value={valor}
          onChange={(e) => onChange(campo.id, e.target.value)}
          className={inputBase}
          placeholder={campo.placeholder}
        />
      );
    case 'cnpj':
      return (
        <input
          type="text"
          value={valor}
          onChange={(e) => onChange(campo.id, e.target.value)}
          className={inputBase}
          placeholder={campo.placeholder || '00.000.000/0000-00'}
          maxLength={18}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={valor}
          onChange={(e) => onChange(campo.id, e.target.value)}
          className={`${inputBase} resize-none`}
          placeholder={campo.placeholder}
          rows={2}
        />
      );
    case 'select':
      return (
        <input
          type="text"
          value={valor}
          onChange={(e) => onChange(campo.id, e.target.value)}
          className={inputBase}
          placeholder={campo.placeholder || 'Selecione...'}
        />
      );
    default:
      return (
        <input
          type="text"
          value={valor}
          onChange={(e) => onChange(campo.id, e.target.value)}
          className={inputBase}
          placeholder={campo.placeholder}
        />
      );
  }
}

function ProfissaoTags({
  profissoes,
  compact,
}: {
  profissoes: { id: string; nome: string }[];
  compact?: boolean;
}) {
  if (profissoes.length <= 1) return null;
  return (
    <div className={`flex flex-wrap ${compact ? 'gap-0.5' : 'gap-1'} mt-1`}>
      {profissoes.map((p) => (
        <span
          key={p.id}
          className={`inline-block rounded-full font-medium border bg-accent-50 text-accent-700 border-accent-200 ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-[11px]'}`}
        >
          {p.nome}
        </span>
      ))}
    </div>
  );
}

function FuncionarioDetailPage() {
  const { id: condoId, funcionarioId } = useParams<{ id: string; funcionarioId: string }>();
  const { effectiveAccountId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [condoNome, setCondoNome] = useState('');

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [dataAdmissao, setDataAdmissao] = useState('');
  const [ultimoExame, setUltimoExame] = useState('');

  const [profissoesIds, setProfissoesIds] = useState<string[]>([]);
  const [camposDinamicos, setCamposDinamicos] = useState<Record<string, string>>({});
  const [treinamentos, setTreinamentos] = useState<any[]>([]);
  // Os anexos do funcionario vem numa consulta so e sao distribuidos pelos dois
  // blocos pelo vinculo com o catalogo (migration 0055). Uma consulta por linha
  // seria uma por treinamento e uma por documento esperado, a cada render.
  const [anexos, setAnexos] = useState<Documento[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!condoId || !funcionarioId) return;
    Promise.all([
      supabase.from('condominios').select('nome').eq('id', condoId).single(),
      supabase.from('funcionarios').select('*').eq('id', funcionarioId).single(),
    ])
      .then(([condoRes, funcRes]) => {
        if (condoRes.error || funcRes.error) {
          setError('Erro ao carregar dados.');
          setLoading(false);
          return;
        }
        setCondoNome(condoRes.data?.nome || '');
        const f = funcRes.data;
        setNome(f?.nome || '');
        setTelefone(f?.telefone || '');
        setEmail(f?.email || '');
        setCpf(f?.cpf || '');
        setRg(f?.rg || '');
        setDataNascimento(f?.data_nascimento || '');
        setDataAdmissao(f?.data_admissao || '');
        setUltimoExame(f?.ultimo_exame || '');
        setProfissoesIds(f?.profissoes_ids || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Erro ao carregar dados.');
        setLoading(false);
      });
  }, [condoId, funcionarioId]);

  // Treinamentos concluídos são registro próprio (migration 0021), e não mais um
  // array dentro do funcionário. A tela lê e escreve nessa tabela.
  const carregarTreinamentos = useCallback(async () => {
    if (!funcionarioId) return;

    const { data, error: erroTreinos } = await supabase
      .from('funcionarios_treinamentos')
      .select('id, treinamento_id, data_conclusao, vigencia_dias')
      .eq('funcionario_id', funcionarioId);

    if (erroTreinos) {
      showToast(`Erro ao carregar treinamentos: ${erroTreinos.message}`, 'error');
      return;
    }

    setTreinamentos(
      (data ?? []).map((linha) => ({
        id: linha.treinamento_id,
        registroId: linha.id,
        dataConclusao: linha.data_conclusao,
        vigenciaDias: linha.vigencia_dias,
      })),
    );
  }, [funcionarioId]);

  useEffect(() => {
    void carregarTreinamentos();
  }, [carregarTreinamentos]);

  const carregarAnexos = useCallback(async () => {
    if (!funcionarioId) {
      setAnexos([]);
      return;
    }
    try {
      setAnexos(await listarDocumentos({ tipo: 'funcionario', id: funcionarioId }));
    } catch (e: any) {
      showToast(`Erro ao carregar anexos: ${e.message}`, 'error');
    }
  }, [funcionarioId]);

  useEffect(() => {
    void carregarAnexos();
  }, [carregarAnexos]);

  /** Certificado anexado a um treinamento, se houver. */
  const anexoDoTreinamento = (treinoId: string) =>
    anexos.find((d) => d.treinamentoId === treinoId) ?? null;

  /** Arquivo que satisfaz um documento esperado pela profissao, se houver. */
  const anexoDoDocumentoEsperado = (docId: string) =>
    anexos.find((d) => d.profissaoDocumentoId === docId) ?? null;

  // Profissões e treinamentos vêm do banco (ADR-0011). A profissão ainda usa o
  // tipo do mock porque `documentos` e `campos` seguem como jsonb dentro dela —
  // é a próxima peça a normalizar.
  const { itens: profissoesCatalogo } = useCatalogo<Profissao>('profissoes');
  const { itens: treinamentosCatalogo } = useCatalogo<TreinamentoCatalogo>('treinamentos');
  const janelaTreinamento = useJanelaDeAlerta('treinamento');

  const profissoesAtivas = useMemo(
    () => profissoesCatalogo.filter((p) => p.ativo),
    [profissoesCatalogo],
  );

  const profissoesSelecionadas = useMemo(
    () => profissoesAtivas.filter((p) => profissoesIds.includes(p.id)),
    [profissoesAtivas, profissoesIds],
  );

  /**
   * Profissões desta pessoa que exigem registro em conselho (secao 6).
   *
   * A exigência vem do catálogo (`profissoes.requer_registro_profissional`); o
   * documento vive aqui, na ficha, porque é dado de uma pessoa — guardá-lo no
   * catálogo global o exporia a todos os Tenants.
   */
  const registrosExigidos = useMemo(
    () => profissoesSelecionadas.filter((p) => p.requerRegistroProfissional),
    [profissoesSelecionadas],
  );

  const mergedCampos = useMemo(() => mergeCampos(profissoesSelecionadas), [profissoesSelecionadas]);
  const mergedDocumentos = useMemo(
    () => mergeDocumentos(profissoesSelecionadas),
    [profissoesSelecionadas],
  );
  const mergedTreinamentos = useMemo(
    () => mergeTreinamentos(profissoesSelecionadas, treinamentosCatalogo),
    [profissoesSelecionadas, treinamentosCatalogo],
  );

  useEffect(() => {
    if (profissoesSelecionadas.length === 0) return;
    setCamposDinamicos((prev) => {
      const novo: Record<string, string> = {};
      mergedCampos.forEach((c) => {
        novo[c.id] = prev[c.id] || '';
      });
      return novo;
    });
  }, [mergedCampos, profissoesSelecionadas.length]);

  useEffect(() => {
    if (profissoesSelecionadas.length === 0) {
      setTreinamentos([]);
      return;
    }
    setTreinamentos((prev) => {
      const mapaExistentes = new Map(prev.map((t: any) => [t.id, t]));
      return mergedTreinamentos.map((t) => {
        const existente = mapaExistentes.get(t.id);
        if (existente) return existente;
        return {
          id: t.id,
          nome: t.nome,
          dataConclusao: '',
          vigenciaDias: t.vigenciaDias,
          obrigatorio: t.obrigatorio,
          descricao: t.descricao,
        };
      });
    });
  }, [mergedTreinamentos, profissoesSelecionadas.length]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleProfissao = (pid: string) => {
    setProfissoesIds((prev) =>
      prev.includes(pid) ? prev.filter((id) => id !== pid) : [...prev, pid],
    );
  };

  const showToast = (msg: string, type?: 'success' | 'error') => {
    setToast({ msg, type: type || 'success' });
    setTimeout(() => setToast(null), 2500);
  };

  const handleCampoChange = (campoId: string, value: string) => {
    setCamposDinamicos((prev) => ({ ...prev, [campoId]: value }));
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      showToast('Nome do funcionário é obrigatório.', 'error');
      return;
    }

    // Os campos pessoais sao opcionais, entao so se valida o que foi
    // preenchido. Antes nenhum deles era conferido: CPF com 3 digitos e data de
    // nascimento em 2099 entravam no banco e so apareciam depois, num relatorio
    // ou numa emissao de documento.
    const problema = [
      cpf.trim() && !validarCpf(cpf) ? MENSAGEM.cpf : null,
      rg.trim() && !validarRg(rg) ? MENSAGEM.rg : null,
      telefone.trim() && !validarTelefone(telefone) ? MENSAGEM.telefone : null,
      email.trim() && !validarEmail(email) ? MENSAGEM.email : null,
      dataNascimento && !dataValida(dataNascimento) ? MENSAGEM_DATA.invalida : null,
      dataNascimento && dataValida(dataNascimento) && noFuturo(dataNascimento)
        ? `Data de nascimento: ${MENSAGEM_DATA.naoPodeSerFutura}`
        : null,
    ].find(Boolean);

    if (problema) {
      showToast(problema, 'error');
      return;
    }

    setSaving(true);
    const payload: Record<string, any> = {
      nome: nome.trim(),
      telefone: telefone || null,
      email: email || null,
      cpf: cpf || null,
      rg: rg || null,
      data_nascimento: dataNascimento || null,
      data_admissao: dataAdmissao || null,
      ultimo_exame: ultimoExame || null,
      profissoes_ids: profissoesIds,
    };
    const { error: updErr } = await supabase
      .from('funcionarios')
      .update(payload)
      .eq('id', funcionarioId);
    setSaving(false);
    if (updErr) {
      showToast(`Erro ao salvar: ${updErr.message}`, 'error');
      return;
    }
    showToast(`Dados de "${nome}" salvos com sucesso.`);
  };

  /**
   * Registra ou atualiza a conclusão de um treinamento.
   *
   * Grava na hora, e não junto com o botão Salvar: a data de conclusão é um
   * fato isolado, e perder o registro porque a pessoa fechou a tela sem salvar
   * seria o mesmo que não ter registrado. Limpar o campo remove o registro.
   */
  const handleDataConclusaoChange = async (treinoId: string, data: string) => {
    if (!funcionarioId) return;

    const doCatalogo = mergedTreinamentos.find((t) => t.id === treinoId);
    const existente = treinamentos.find((t: any) => t.id === treinoId);

    if (!data) {
      if (existente?.registroId) {
        const { error: erroDel } = await supabase
          .from('funcionarios_treinamentos')
          .delete()
          .eq('id', existente.registroId);
        if (erroDel) {
          showToast(`Erro ao remover: ${erroDel.message}`, 'error');
          return;
        }
      }
      await carregarTreinamentos();
      showToast('Registro de treinamento removido.');
      return;
    }

    if (!dataValida(data)) {
      showToast(MENSAGEM_DATA.invalida, 'error');
      return;
    }
    if (noFuturo(data)) {
      showToast('A conclusão do treinamento não pode ser uma data futura.', 'error');
      return;
    }

    const { error: erroSave } = await supabase.from('funcionarios_treinamentos').upsert(
      {
        id: existente?.registroId,
        account_id: effectiveAccountId,
        funcionario_id: funcionarioId,
        treinamento_id: treinoId,
        data_conclusao: data,
        // A vigência do catálogo vale para o registro novo; a do registro que já
        // existe é preservada, porque foi sob ela que o certificado saiu.
        vigencia_dias: existente?.vigenciaDias || doCatalogo?.vigenciaDias || 365,
        editado_em: new Date().toISOString(),
      },
      { onConflict: 'id' },
    );

    if (erroSave) {
      showToast(`Erro ao registrar: ${erroSave.message}`, 'error');
      return;
    }

    await carregarTreinamentos();
    showToast('Conclusão registrada.');
  };

  const formatData = (iso: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const statusTreinoBadge = (status: string) => {
    switch (status) {
      case 'vigente':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'a_vencer':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'vencido':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-background-100 text-foreground-700 border-background-300';
    }
  };

  const treinamentosComStatus = useMemo(() => {
    return treinamentos.map((t: any) => {
      if (!t.dataConclusao)
        return {
          ...t,
          dataVencimento: null,
          status: { status: 'pendente', label: 'Pendente', dias: 0 },
        };
      const dataVenc = calcularDataVencimento(t.dataConclusao, t.vigenciaDias);
      const status = obterStatusTreinamento(dataVenc, janelaTreinamento);
      return { ...t, dataVencimento: dataVenc, status };
    });
  }, [treinamentos, janelaTreinamento]);

  const contagemTreinamentos = useMemo(() => {
    return {
      total: treinamentosComStatus.length,
      vigente: treinamentosComStatus.filter((t: any) => t.status.status === 'vigente').length,
      a_vencer: treinamentosComStatus.filter((t: any) => t.status.status === 'a_vencer').length,
      vencido: treinamentosComStatus.filter((t: any) => t.status.status === 'vencido').length,
      pendente: treinamentosComStatus.filter((t: any) => t.status.status === 'pendente').length,
    };
  }, [treinamentosComStatus]);

  const treinosVencidos = treinamentosComStatus.filter((t: any) => t.status.status === 'vencido');
  const treinosAVencer = treinamentosComStatus.filter((t: any) => t.status.status === 'a_vencer');

  const camposObrigatorios = mergedCampos.filter((c) => c.obrigatorio).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl flex flex-col items-center gap-3 py-20">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-600">{error}</p>
          <Link
            to={`/condominios/${condoId}/funcionarios`}
            className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
          >
            Voltar
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl">
        {/* breadcrumb */}
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/condominios"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
              Condomínios
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <Link
              to={`/condominios/${condoId}/funcionarios`}
              className="text-sm text-foreground-600 hover:text-foreground-800 transition-colors"
            >
              {condoNome}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            <span className="text-sm font-semibold text-foreground-900">{nome}</span>
          </div>
        </div>

        <h1 className="text-xl font-bold text-foreground-900 mb-6">Ficha do Funcionário</h1>

        {/* Dados Pessoais */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center">
              <i className="ri-user-line text-primary-600 text-xs"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900">Dados Pessoais</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nome completo *
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">CPF</label>
              <input
                type="text"
                value={cpf}
                onChange={(e) => setCpf(mascaraCpf(e.target.value))}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">RG</label>
              <input
                type="text"
                value={rg}
                onChange={(e) => setRg(mascaraRg(e.target.value))}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Data de Nascimento
              </label>
              <input
                type="date"
                value={dataNascimento}
                onChange={(e) => setDataNascimento(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Telefone
              </label>
              <input
                type="text"
                value={telefone}
                onChange={(e) => setTelefone(mascaraTelefone(e.target.value))}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Dados Profissionais */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-accent-100 flex items-center justify-center">
              <i className="ri-briefcase-line text-accent-700 text-xs"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900">Dados Profissionais</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="md:col-span-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Funções *
                {profissoesIds.length > 0 && (
                  <span className="text-xs text-foreground-400 ml-1">
                    ({profissoesIds.length} selecionada{profissoesIds.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-left flex items-center justify-between min-h-[42px] hover:border-background-400/60 transition-colors"
                >
                  <div className="flex flex-wrap gap-1.5 pr-2">
                    {profissoesIds.length === 0 ? (
                      <span className="text-foreground-400">Selecione as funções...</span>
                    ) : (
                      profissoesSelecionadas.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200"
                        >
                          {p.nome}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleProfissao(p.id);
                            }}
                            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-primary-100 transition-colors"
                          >
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`}
                  ></i>
                </button>

                {showDropdown && (
                  <div className="absolute z-20 mt-1 w-full bg-background-50 border border-background-200/70 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    <div className="p-1.5">
                      {profissoesAtivas.map((p) => {
                        const checked = profissoesIds.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${checked ? 'bg-primary-50/50' : 'hover:bg-background-100'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleProfissao(p.id)}
                              className="sr-only peer"
                            />
                            <div
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${checked ? 'bg-primary-500 border-primary-500' : 'border-background-300'}`}
                            >
                              <i
                                className={`ri-check-line text-background-50 text-[10px] leading-none transition-transform duration-200 ${checked ? 'scale-100' : 'scale-0'}`}
                              ></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground-800 truncate">
                                {p.nome}
                              </p>
                              <p className="text-xs text-foreground-500 truncate">
                                {p.descricao.slice(0, 60)}
                                {p.descricao.length > 60 ? '...' : ''}
                              </p>
                            </div>
                            <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              {categoriaProfissaoLabels[p.categoria] || p.categoria}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {profissoesIds.length > 0 && (
                      <div className="px-3 py-2 border-t border-background-200/70">
                        <button
                          type="button"
                          onClick={() => setProfissoesIds([])}
                          className="text-xs text-red-600 hover:text-red-700 font-medium transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-circle-line mr-1"></i>
                          Remover todas as funções
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Data de Admissão
              </label>
              <input
                type="date"
                value={dataAdmissao}
                onChange={(e) => setDataAdmissao(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Último Exame Periódico
              </label>
              <input
                type="date"
                value={ultimoExame}
                onChange={(e) => setUltimoExame(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
          </div>

          {/* Campos dinâmicos das profissões */}
          {profissoesSelecionadas.length > 0 && mergedCampos.length > 0 && (
            <div className="pt-5 border-t border-background-200/70">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-secondary-100 flex items-center justify-center">
                  <i className="ri-input-field text-secondary-700 text-xs"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">Campos das Funções</h3>
                <span className="text-xs text-foreground-400">
                  ({mergedCampos.length} campos — {camposObrigatorios} obrigatórios)
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mergedCampos.map((campo) => (
                  <div key={campo.id} className={campo.tipo === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {campo.nome}
                      {campo.obrigatorio && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {renderCampoDinamico(campo, camposDinamicos[campo.id] || '', handleCampoChange)}
                    {campo.profissoes.length > 1 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {campo.profissoes.map((p) => (
                          <span
                            key={p.id}
                            className="inline-block px-1.5 py-0 rounded-full text-[10px] font-medium border bg-accent-50 text-accent-700 border-accent-200"
                          >
                            {p.nome}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {profissoesSelecionadas.length === 0 && (
            <div className="pt-5 border-t border-background-200/70">
              <div className="flex items-start gap-3 p-4 bg-amber-50/60 border border-amber-200/60 rounded-lg">
                <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-600 text-xs"></i>
                </div>
                <div>
                  <p className="text-sm text-amber-800 font-medium">
                    Selecione ao menos uma função
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Ao selecionar uma ou mais profissões, os campos específicos, documentos
                    esperados e treinamentos de todas as funções serão carregados e combinados
                    automaticamente.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Documentos Esperados */}
        {profissoesSelecionadas.length > 0 && mergedDocumentos.length > 0 && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center">
                <i className="ri-file-list-line text-primary-600 text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900">Documentos Esperados</h3>
              <span className="text-xs text-foreground-500">
                ({mergedDocumentos.length} documentos —{' '}
                {mergedDocumentos.filter((d) => d.obrigatorio).length} obrigatórios)
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {mergedDocumentos.map((doc) => (
                <div
                  key={doc.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md border ${
                    doc.obrigatorio
                      ? 'bg-red-50/40 border-red-200'
                      : 'bg-background-100/50 border-background-200/70'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                      doc.obrigatorio ? 'bg-red-100' : 'bg-background-100'
                    }`}
                  >
                    <i
                      className={`${doc.obrigatorio ? 'ri-file-warning-line text-red-600' : 'ri-file-line text-foreground-400'} text-xs`}
                    ></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-foreground-800 truncate">{doc.nome}</p>
                      <span
                        className={`text-xs flex-shrink-0 ${doc.obrigatorio ? 'text-red-600 font-medium' : 'text-foreground-400'}`}
                      >
                        {doc.obrigatorio ? 'Obrigatório' : 'Opcional'}
                      </span>
                    </div>
                    {doc.profissoes.length > 1 && (
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {doc.profissoes.map((p) => (
                          <span
                            key={p.id}
                            className="inline-block px-1.5 py-0 rounded-full text-[10px] font-medium border bg-accent-50 text-accent-700 border-accent-200"
                          >
                            {p.nome}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* A lista dizia o que falta e nao aceitava o que existe. */}
                  {funcionarioId ? (
                    <AnexoDoItem
                      documento={anexoDoDocumentoEsperado(doc.id)}
                      dono={{ tipo: 'funcionario', id: funcionarioId }}
                      tipo="avulso"
                      nome={doc.nome}
                      profissaoDocumentoId={doc.id}
                      onMudou={() => void carregarAnexos()}
                    />
                  ) : (
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                      Grave para anexar
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registro profissional (secao 6) */}
        {registrosExigidos.length > 0 && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded bg-red-50 flex items-center justify-center">
                <i className="ri-shield-user-line text-red-600 text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900">Registro profissional</h3>
            </div>
            <p className="text-xs text-foreground-500 mb-4">
              {registrosExigidos.map((p) => p.tipoRegistroProfissional).join(', ')} — exigido pela
              função selecionada. O número, a validade e o arquivo ficam aqui, na ficha da pessoa; o
              catálogo de profissões só declara a exigência.
            </p>

            {funcionarioId ? (
              registrosExigidos.map((prof) => (
                <div key={prof.id} className="mb-3 last:mb-0">
                  <DocumentosDoRegistro
                    dono={{ tipo: 'funcionario', id: funcionarioId }}
                    condominioId={condoId ?? ''}
                    titulo={`${prof.tipoRegistroProfissional ?? 'Registro'} — ${prof.nome}`}
                    tipos={['registro_profissional']}
                    chaveDeAlerta="treinamento"
                    profissaoId={prof.id}
                  />
                </div>
              ))
            ) : (
              <p className="text-xs text-foreground-500">
                Grave o funcionário para anexar o registro.
              </p>
            )}
          </div>
        )}

        {/* Treinamentos */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-accent-100 flex items-center justify-center">
              <i className="ri-award-line text-accent-700 text-xs"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900">
              Treinamentos e Certificações
            </h3>
            {profissoesSelecionadas.length > 0 && (
              <span className="text-xs text-foreground-500">
                ({contagemTreinamentos.total} treinamentos)
              </span>
            )}
          </div>

          {profissoesSelecionadas.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
                <i className="ri-award-line text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">
                Selecione ao menos uma função para visualizar os treinamentos.
              </p>
            </div>
          ) : (
            <>
              {/* Cards de resumo */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                {[
                  {
                    label: 'Total',
                    valor: contagemTreinamentos.total,
                    cor: 'bg-background-50 border-background-200/70',
                    texto: 'text-foreground-900',
                  },
                  {
                    label: 'Vigentes',
                    valor: contagemTreinamentos.vigente,
                    cor: 'bg-green-50 border-green-200',
                    texto: 'text-green-700',
                  },
                  {
                    label: 'A Vencer',
                    valor: contagemTreinamentos.a_vencer,
                    cor: 'bg-amber-50 border-amber-200',
                    texto: 'text-amber-700',
                  },
                  {
                    label: 'Vencidos',
                    valor: contagemTreinamentos.vencido,
                    cor: 'bg-red-50 border-red-200',
                    texto: 'text-red-700',
                  },
                  {
                    label: 'Pendentes',
                    valor: contagemTreinamentos.pendente,
                    cor: 'bg-secondary-50 border-secondary-200',
                    texto: 'text-secondary-900',
                  },
                ].map((card) => (
                  <div key={card.label} className={`${card.cor} border rounded-lg p-3 md:p-4`}>
                    <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                    <p className={`text-xl md:text-2xl font-bold mt-1 ${card.texto}`}>
                      {card.valor}
                    </p>
                  </div>
                ))}
              </div>

              {/* Alertas */}
              {(treinosVencidos.length > 0 || treinosAVencer.length > 0) && (
                <div className="flex flex-col sm:flex-row gap-3 mb-5">
                  {treinosVencidos.length > 0 && (
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line text-red-600 text-sm"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-red-800">
                          {treinosVencidos.length} treinamento(s) vencido(s)
                        </p>
                        <p className="text-xs text-red-600 mt-0.5 truncate">
                          {treinosVencidos
                            .map((t: any) => t.nome)
                            .slice(0, 2)
                            .join(', ')}
                          {treinosVencidos.length > 2
                            ? ` e mais ${treinosVencidos.length - 2}`
                            : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  {treinosAVencer.length > 0 && (
                    <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-timer-line text-amber-600 text-sm"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-800">
                          {treinosAVencer.length} com vencimento em até 30 dias
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5 truncate">
                          {treinosAVencer
                            .map((t: any) => t.nome)
                            .slice(0, 2)
                            .join(', ')}
                          {treinosAVencer.length > 2 ? ` e mais ${treinosAVencer.length - 2}` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tabela de treinamentos */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap">
                        Treinamento
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap">
                        Conclusão
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap hidden sm:table-cell">
                        Vencimento
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap hidden md:table-cell">
                        Vigência
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap">
                        Data
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-3 py-3 whitespace-nowrap">
                        Certificado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {treinamentosComStatus.map((treino: any) => {
                      const mergedSource = mergedTreinamentos.find((mt) => mt.id === treino.id);
                      return (
                        <tr
                          key={treino.id}
                          className={`hover:bg-background-100/30 transition-colors ${
                            treino.status.status === 'vencido'
                              ? 'bg-red-50/30'
                              : treino.status.status === 'a_vencer'
                                ? 'bg-amber-50/30'
                                : treino.status.status === 'pendente'
                                  ? 'bg-background-100/30'
                                  : ''
                          }`}
                        >
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div
                                className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                                  treino.status.status === 'vencido'
                                    ? 'bg-red-100'
                                    : treino.status.status === 'a_vencer'
                                      ? 'bg-amber-100'
                                      : treino.status.status === 'vigente'
                                        ? 'bg-green-100'
                                        : 'bg-background-200'
                                }`}
                              >
                                <i
                                  className={`text-xs ${
                                    treino.status.status === 'vencido'
                                      ? 'ri-close-circle-line text-red-600'
                                      : treino.status.status === 'a_vencer'
                                        ? 'ri-time-line text-amber-600'
                                        : treino.status.status === 'vigente'
                                          ? 'ri-check-line text-green-600'
                                          : 'ri-more-line text-foreground-400'
                                  }`}
                                ></i>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground-800 truncate">
                                  {treino.nome}
                                </p>
                                {treino.descricao && (
                                  <p className="text-xs text-foreground-500 truncate hidden sm:block">
                                    {treino.descricao}
                                  </p>
                                )}
                                {mergedSource && mergedSource.profissoes.length > 1 && (
                                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                                    {mergedSource.profissoes.map((p) => (
                                      <span
                                        key={p.id}
                                        className="inline-block px-1.5 py-0 rounded-full text-[10px] font-medium border bg-accent-50 text-accent-700 border-accent-200"
                                      >
                                        {p.nome}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`text-sm ${treino.dataConclusao ? 'text-foreground-700' : 'text-foreground-400'}`}
                            >
                              {formatData(treino.dataConclusao)}
                            </span>
                          </td>
                          <td className="px-3 py-3 hidden sm:table-cell">
                            <span
                              className={`text-sm ${
                                !treino.dataVencimento
                                  ? 'text-foreground-400'
                                  : treino.status.status === 'vencido'
                                    ? 'text-red-600 font-semibold'
                                    : treino.status.status === 'a_vencer'
                                      ? 'text-amber-600 font-medium'
                                      : 'text-foreground-600'
                              }`}
                            >
                              {formatData(treino.dataVencimento)}
                            </span>
                          </td>
                          <td className="px-3 py-3 hidden md:table-cell">
                            <span className="text-sm text-foreground-600">
                              {/*
                                Duas fontes, e a diferença é a regra da secao 7.

                                Treinamento **concluído** mostra a vigência que
                                foi congelada no registro (migration 0021): um
                                certificado emitido sob regra de um ano não passa
                                a valer dois porque a norma mudou depois.

                                Treinamento **pendente** mostra a do catálogo, que
                                é a que valerá quando ele for feito.

                                Nos dois casos o rótulo sai de `periodoTexto`, e
                                não de uma divisão por 365 — que transformava
                                "18 meses" em "1.5 ano(s)", um número que ninguém
                                digitou.
                              */}
                              {periodoTexto(
                                treino.dataConclusao
                                  ? diasEmPeriodo(treino.vigenciaDias)
                                  : (mergedSource?.vigencia ?? { valor: null, unidade: null }),
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusTreinoBadge(treino.status.status)}`}
                              >
                                {treino.status.label}
                              </span>
                              {treino.obrigatorio && (
                                <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-red-50 text-red-700 border-red-200 whitespace-nowrap">
                                  Obrig
                                </span>
                              )}
                            </div>
                            {treino.status.status === 'vencido' && (
                              <p className="text-xs mt-0.5 text-red-600">
                                Vencido há {treino.status.dias} dia(s)
                              </p>
                            )}
                            {treino.status.status === 'a_vencer' && (
                              <p className="text-xs mt-0.5 text-amber-600">
                                Vence em {treino.status.dias} dia(s)
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <input
                              type="date"
                              value={treino.dataConclusao}
                              onChange={(e) => handleDataConclusaoChange(treino.id, e.target.value)}
                              className="px-2 py-1.5 rounded-md border border-background-300/60 bg-background-50 text-xs w-[130px]"
                            />
                          </td>
                          {/* Na fiscalizacao o que se pede e o certificado, nao
                              a data que alguem digitou. */}
                          <td className="px-3 py-3 text-right">
                            {funcionarioId ? (
                              <AnexoDoItem
                                documento={anexoDoTreinamento(treino.id)}
                                dono={{ tipo: 'funcionario', id: funcionarioId }}
                                tipo="treinamento"
                                nome={treino.nome}
                                treinamentoId={treino.id}
                                onMudou={() => void carregarAnexos()}
                              />
                            ) : (
                              <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                                Grave para anexar
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Salvando...' : 'Salvar Funcionário'}
          </button>
        </div>
      </div>

      {/* toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : 'ri-close-circle-line text-red-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default FuncionarioDetailPage;
